import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useNavigate } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { useAuthContext } from "@/contexts/AuthContext"
import { useCountries } from "@/graphql/hooks/Locations/useLocations"
import { useRegions } from "@/graphql/hooks/Locations/useLocations"
import { useLocations } from "@/graphql/hooks/Locations/useLocations"
import { useStoreRegister } from "@/graphql/hooks/useStoreRegister"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Store } from "lucide-react"
import { LoadingScreen } from "@/components/ui/loading-spinner"

const storeSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  inChargePerson: z.string().min(2, "La persona encargada es requerida"),
  description: z.string().optional(),
  phone: z.string().min(8, "El teléfono debe tener al menos 8 caracteres"),
  email: z.string().email("Email inválido"),
  siteUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  facebookUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  instagramUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  youtubeUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  twitterUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  address: z.object({
    address: z.string().min(5, "La dirección es requerida"),
    countryId: z.number().min(1, "El país es requerido"),
    regionId: z.number().min(1, "La región es requerida").optional().or(z.literal(0)),
    locationId: z.number().min(1, "La ciudad es requerida").optional().or(z.literal(0)),
  }),
})

type StoreFormValues = z.infer<typeof storeSchema>

export const CreateStore = () => {
  const navigate = useNavigate()
  const { person, isAuthenticated, loading } = useAuthContext()
  const { registerStore, loading: registerLoading } = useStoreRegister()

  const [selectedCountry, setSelectedCountry] = useState(0)

  const { countries, loading: loadingCountries } = useCountries()

  const form = useForm<StoreFormValues>({
    resolver: zodResolver(storeSchema),
    defaultValues: {
      name: "",
      inChargePerson: person?.fullName || "",
      description: "",
      phone: "",
      email: person?.email || "",
      siteUrl: "",
      facebookUrl: "",
      instagramUrl: "",
      youtubeUrl: "",
      twitterUrl: "",
      address: {
        address: "",
        countryId: 0,
        regionId: 0,
        locationId: 0,
      },
    },
  })

  const { regions } = useRegions(selectedCountry)
  const { locations, refetchLocations } = useLocations()

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/login")
      return
    }

    if (person?.Store?.active) {
      navigate("/profile")
      return
    }
  }, [isAuthenticated, loading, person, navigate])

  const onSubmit = async (values: StoreFormValues) => {
    try {
      // Ensure required fields are present
      const storeInput = {
        ...values,
        name: values.name!,
        inChargePerson: values.inChargePerson!,
        phone: values.phone!,
        email: values.email!,
        address: {
          ...values.address!,
          name: "Casa Matriz",
          address: values.address!.address,
          countryId: values.address!.countryId,
          regionId: values.address!.regionId,
          locationId: values.address!.locationId,
        },
      }

      const result = await registerStore(storeInput)
      if (result?.StoreRegister?.status?.success) {
        navigate("/profile")
      }
    } catch (error) {
      console.error("Error registering store:", error)
    }
  }

  if (loadingCountries) {
    return <LoadingScreen />
  }

  if (!person?.active) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">Solo usuarios activos pueden crear tiendas</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate("/profile")} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Perfil
          </Button>

          <div className="flex items-center gap-3 mb-2">
            <Store className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Crear Nueva Tienda</h1>
          </div>
          <p className="text-muted-foreground">Completa la información para crear tu tienda</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Información de la Tienda</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre de la Tienda *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="inChargePerson"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Persona Encargada *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Redes Sociales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="siteUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sitio Web</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="facebookUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Facebook</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://facebook.com/..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="instagramUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Instagram</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://instagram.com/..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="twitterUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Twitter</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://twitter.com/..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Dirección</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="address.address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Dirección *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Av. Principal 123" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address.countryId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>País *</FormLabel>
                          <Select
                            value={field.value?.toString()}
                            onValueChange={(value) => {
                              // refetchRegions(parseInt(value));
                              setSelectedCountry(parseInt(value))
                              field.onChange(parseInt(value))
                            }}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar país" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {countries?.map((country) => (
                                <SelectItem key={country.id} value={country.id.toString()}>
                                  {country.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address.regionId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Región *</FormLabel>
                          <Select
                            value={field.value?.toString()}
                            onValueChange={(value) => {
                              field.onChange(parseInt(value))
                              refetchLocations(parseInt(value))
                            }}
                            disabled={!regions.length}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar región" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {regions?.map((region) => (
                                <SelectItem key={region.id} value={region.id.toString()}>
                                  {region.fullName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address.locationId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ciudad *</FormLabel>
                          <Select
                            value={field.value?.toString()}
                            onValueChange={(value) => {
                              field.onChange(parseInt(value))
                            }}
                            disabled={!locations.length}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar ciudad" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {locations?.map((location) => (
                                <SelectItem key={location.id} value={location.id.toString()}>
                                  {location.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/profile")}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={registerLoading} className="flex-1">
                    {registerLoading ? "Creando..." : "Crear Tienda"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
